<template>
  <div class="ta-root">
  <div class="ta-page">

    <!-- ══════════   ════════════════════════════════════════════════════
         HERO — full-width image, centred serif title + italic sub
    ══════════════════════════════════════════════════════════════ -->
    <section class="ta-hero">
      <img
        class="ta-hero__bg"
        src="https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Photo-banner.webp"
        alt="Paris vue depuis un balcon"
      />
      <div class="ta-hero__overlay"></div>
      <div class="ta-hero__content">
        <h1 class="ta-hero__title" :class="isPhone ? 'merriweather-reg-26' : 'merriweather-reg-32'">
          Le premier DMC de luxe <br v-if="isPhone">éco-responsable en France
        </h1>
        <p class="ta-hero__sub">
          <span :class="isPhone ? 'merriweather-light-18' : 'merriweather-light-24'">La sélection des </span><span :class="isPhone ? 'merriweather-light-italic-18' : 'merriweather-light-italic-24'"><em>plus beaux hébergements éco-responsables</em></span>
        </p>
      </div>
    </section>

    <!-- ══════════════════════════════════════════════════════════════
         INTRO — light grey band, centred body paragraph
    ══════════════════════════════════════════════════════════════ -->
    <section class="ta-intro">
      <p class="ta-intro__text raleway-reg-p15">
        Hortense est à la fois une plateforme de réservation et une Destination Management Company (DMC),
        spécialisée dans la création de séjours durables et sur mesure à travers la France. Nous sélectionnons
        avec exigence des hébergements engagés et concevons, aux côtés de nos partenaires, des expériences
        uniques, pensées pour une clientèle en quête de sens, d'authenticité et d'excellence.
      </p>
    </section>

    <!-- ══════════════════════════════════════════════════════════════
         SECTION 2 — POURQUOI CHOISIR HORTENSE ?
         White bg · centred heading + italic sub · 2×3 card grid
         Each card = rounded white box (border + shadow) containing
         image at top then title + body text
    ══════════════════════════════════════════════════════════════ -->
    <section class="ta-why">
      <div class="ta-why__header">
        <h2 :class="isPhone ? 'merriweather-reg-20' : 'merriweather-reg-26'">
          Pourquoi choisir Hortense&nbsp;?
        </h2>
      </div>

      <div class="ta-why__grid">
        <article class="ta-why-card" v-for="(card, i) in whyCards" :key="i">
          <div class="ta-why-card__img-wrap">
            <img :src="card.img" :alt="card.title" loading="lazy" />
          </div>
          <div class="ta-why-card__body">
            <h3 class="ta-why-card__title raleway-semi-p15">{{ card.title }}</h3>
            <p class="ta-why-card__text raleway-reg-p15">{{ card.text }}</p>
          </div>
        </article>
      </div>
    </section>

    <!-- ══════════════════════════════════════════════════════════════
         SECTION 3 — POURQUOI VOS CLIENTS VONT ADORER HORTENSE ?
         5 columns, NO border-radius on images, NO gap between images.
         Centre column tallest (starts at top), flanking cols shorter
         (aligned to bottom of centre), outermost cols shortest.
         Text (title + body) sits BELOW each image.
         Left/right arrow buttons.
    ══════════════════════════════════════════════════════════════ -->
    <section class="ta-clients">
      <h2 class="ta-clients__title" :class="isPhone ? 'merriweather-reg-20' : 'merriweather-reg-26'">
        Pourquoi vos clients vont adorer Hortense&nbsp;?
      </h2>

      <div class="ta-clients__wrapper">
        <button class="ta-arr ta-arr--left" @click="clientsPrev" aria-label="Précédent">&#8592;</button>

        <div class="ta-clients__viewport" ref="clientsViewport">
          <div class="ta-clients__track">
            <div
              v-for="(item, i) in clientsItems"
              :key="i"
              class="ta-clients__col"
              :class="i % 2 === 1 ? 'ta-clients__col--offset' : ''"
            >
              <div class="ta-clients__img-box">
                <img :src="item.img" :alt="item.title" loading="lazy" />
              </div>
              <div class="ta-clients__text-box">
                <h3 class="ta-clients__item-title raleway-semi-p15">{{ item.title }}</h3>
                <p class="ta-clients__item-desc raleway-reg-p15">
                  {{ item.text }}
                  <a v-if="item.link" href="#" class="link-hortense">Voir plus</a>
                </p>
              </div>
            </div>
          </div>
        </div>

        <button class="ta-arr ta-arr--right" @click="clientsNext" aria-label="Suivant">&#8594;</button>
      </div>
    </section>

    <!-- ══════════════════════════════════════════════════════════════
         SECTION 4 — NOTRE APPROCHE / NOTRE OBJECTIF
         Two offset image + white-card blocks on grey bg
    ══════════════════════════════════════════════════════════════ -->
    <section class="ta-approach">

      <!-- Block A: white card left overlaps image right -->
      <div class="ta-approach__block ta-approach__block--a">
        <div class="ta-approach__card">
          <h2 class="ta-approach__card-title" :class="isPhone ? 'merriweather-reg-20' : 'merriweather-reg-26'">
            Notre approche
          </h2>
          <p class="ta-approach__card-intro raleway-reg-p15">
            En tant que DMC, Hortense conçoit et orchestre des séjours sur mesure en France, en
            collaboration avec un réseau de partenaires locaux engagés. Nous intervenons en
            complément de votre activité pour&nbsp;:
          </p>
          <ul class="ta-approach__list">
            <li class="raleway-medi-p15">Identifier des hébergements de charme éco-responsables</li>
            <li class="raleway-medi-p15">Concevoir des itinéraires personnalisés</li>
            <li class="raleway-medi-p15">Organiser des expériences exclusives</li>
          </ul>
        </div>
        <div class="ta-approach__img-wrap">
          <img
            src="https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Banner%20-%20notre%20approche.webp"
            alt="Notre approche"
            loading="lazy"
          />
        </div>
      </div>

      <!-- Block B: image left, white card right overlaps -->
      <!-- <div class="ta-approach__block ta-approach__block--b">
        <div class="ta-approach__img-wrap">
          <img
            src="https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Banner%20-%20objectif.webp"
            alt="Notre objectif"
            loading="lazy"
          />
        </div>
        <div class="ta-approach__card">
          <h2 class="ta-approach__card-title" :class="isPhone ? 'merriweather-reg-20' : 'merriweather-reg-26'">
            Notre objectif
          </h2>
          <p class="ta-approach__card-intro raleway-reg-p15">
            Vous permettre d'offrir à vos clients des séjours uniques, parfaitement exécutés
            et alignés avec leurs attentes.
          </p>
        </div>
      </div> -->

    </section>

    <!-- ══════════════════════════════════════════════════════════════
         SECTION 5 — DES EXPÉRIENCES UNIQUES ET SUR MESURE
         Destination carousel — rounded image cards, overlay label
    ══════════════════════════════════════════════════════════════ -->
    <section class="ta-destinations">
      <h2 class="ta-destinations__title" :class="isPhone ? 'merriweather-reg-20' : 'merriweather-reg-26'">
        Des expériences uniques et sur mesure
      </h2>

      <div class="ta-dest__wrapper">
        <button class="ta-arr ta-arr--left" @click="destPrev" aria-label="Précédent">&#8592;</button>
        <div class="ta-dest__viewport" ref="destViewport">
          <div class="ta-dest__track">
            <div v-for="(dest, i) in destItems" :key="i" class="ta-dest__card">
              <img :src="dest.img" :alt="dest.name" loading="lazy" />
              <div class="ta-dest__card-overlay"></div>
              <div class="ta-dest__card-label text-center">
                <span class="ta-dest__card-name" :class="isPhone ? 'raleway-bold-p16' : 'raleway-bold-p16'">{{ dest.name }}</span>
                <span class="ta-dest__card-sub" :class="isPhone ? 'raleway-reg-p15' : 'raleway-reg-p15'">{{ dest.sub }}</span>
              </div>
            </div>
          </div>
        </div>
        <button class="ta-arr ta-arr--right" @click="destNext" aria-label="Suivant">&#8594;</button>
      </div>
    </section>

    <!-- ══════════════════════════════════════════════════════════════
         SECTION 6 — NOUS CONTACTER
    ══════════════════════════════════════════════════════════════ -->
    <section class="ta-contact" id="nous-contacter">
      <h2 :class="isPhone ? 'merriweather-reg-20' : 'merriweather-reg-26'" class="ta-contact__title">
        Nous contacter&nbsp;?
      </h2>
      <p class="ta-contact__text raleway-reg-p15">
        Vous avez une demande spécifique ou souhaitez recevoir une proposition sur mesure&nbsp;? Notre équipe est à votre disposition pour vous accompagner.
      </p>

      <!-- Contact Form -->
      <div class="ta-contact__form-wrap">
        <h3 class="ta-contact__form-title merriweather-reg-22">Formulaire de contact</h3>
        <hr class="ta-contact__form-divider" />

        <form @submit.prevent="handleContactSubmit" class="ta-contact__form">

          <!-- NOM + PRÉNOM -->
          <div class="ta-form-row">
            <div class="ta-form-group">
              <label class="ta-form-label raleway-semi-p15" for="ta_last_name">NOM</label>
              <input
                id="ta_last_name"
                v-model="contactForm.last_name"
                type="text"
                class="ta-form-input"
              />
            </div>
            <div class="ta-form-group">
              <label class="ta-form-label raleway-semi-p15" for="ta_first_name">PRÉNOM</label>
              <input
                id="ta_first_name"
                v-model="contactForm.first_name"
                type="text"
                class="ta-form-input"
              />
            </div>
          </div>

          <!-- EMAIL -->
          <div class="ta-form-row ta-form-row--full">
            <div class="ta-form-group">
              <label class="ta-form-label raleway-semi-p15" for="ta_email">EMAIL</label>
              <input
                id="ta_email"
                v-model="contactForm.email"
                type="email"
                class="ta-form-input"
                required
              />
            </div>
          </div>

          <!-- SOCIÉTÉ -->
          <div class="ta-form-row ta-form-row--full">
            <div class="ta-form-group">
              <label class="ta-form-label raleway-semi-p15" for="ta_societe">SOCIÉTÉ</label>
              <input
                id="ta_societe"
                v-model="contactForm.societe"
                type="text"
                class="ta-form-input"
              />
            </div>
          </div>

          <!-- NOMBRE D'ADULTES + NOMBRE D'ENFANTS -->
          <div class="ta-form-row">
            <div class="ta-form-group">
              <label class="ta-form-label raleway-semi-p15" for="ta_adults">NOMBRE D'ADULTES</label>
              <input
                id="ta_adults"
                v-model="contactForm.nombre_adultes"
                type="number"
                min="0"
                class="ta-form-input"
              />
            </div>
            <div class="ta-form-group">
              <label class="ta-form-label raleway-semi-p15" for="ta_children">NOMBRE D'ENFANTS</label>
              <input
                id="ta_children"
                v-model="contactForm.nombre_enfants"
                type="number"
                min="0"
                class="ta-form-input"
              />
            </div>
          </div>

          <!-- ÂGE MOYEN DES CLIENTS -->
          <div class="ta-form-row ta-form-row--full">
            <div class="ta-form-group">
              <label class="ta-form-label raleway-semi-p15" for="ta_age_moyen">ÂGE MOYEN DES CLIENTS</label>
              <input
                id="ta_age_moyen"
                v-model="contactForm.age_moyen"
                type="text"
                class="ta-form-input"
              />
            </div>
          </div>

          <!-- NOMBRE DE CHAMBRES SOUHAITÉES -->
          <div class="ta-form-row ta-form-row--full">
            <div class="ta-form-group">
              <label class="ta-form-label raleway-semi-p15" for="ta_chambres">NOMBRE DE CHAMBRES SOUHAITÉES</label>
              <input
                id="ta_chambres"
                v-model="contactForm.nombre_chambres"
                type="number"
                min="0"
                class="ta-form-input"
              />
            </div>
          </div>

          <!-- DATES DU SÉJOUR + BUDGET TOTAL DU SÉJOUR -->
          <div class="ta-form-row">
            <div class="ta-form-group">
              <label class="ta-form-label raleway-semi-p15" for="ta_dates">DATES DU SÉJOUR</label>
              <input
                id="ta_dates"
                v-model="contactForm.dates_sejour"
                type="text"
                class="ta-form-input"
                placeholder="ex: 10/07 – 17/07/2025"
              />
            </div>
            <div class="ta-form-group">
              <label class="ta-form-label raleway-semi-p15" for="ta_budget">BUDGET TOTAL DU SÉJOUR</label>
              <input
                id="ta_budget"
                v-model="contactForm.budget"
                type="text"
                class="ta-form-input"
                placeholder="ex: 5 000 €"
              />
            </div>
          </div>

          <!-- DESTINATION SOUHAITÉE -->
          <div class="ta-form-row ta-form-row--full">
            <div class="ta-form-group">
              <label class="ta-form-label raleway-semi-p15" for="ta_destination">DESTINATION SOUHAITÉE</label>
              <input
                id="ta_destination"
                v-model="contactForm.destination"
                type="text"
                class="ta-form-input"
              />
            </div>
          </div>

          <!-- TYPE D'EXPÉRIENCE RECHERCHÉE -->
          <div class="ta-form-row ta-form-row--full">
            <div class="ta-form-group">
              <label class="ta-form-label raleway-semi-p15" for="ta_experience">TYPE D'EXPÉRIENCE RECHERCHÉE</label>
              <input
                id="ta_experience"
                v-model="contactForm.type_experience"
                type="text"
                class="ta-form-input"
              />
            </div>
          </div>

          <!-- MESSAGE COMPLÉMENTAIRE -->
          <div class="ta-form-row ta-form-row--full">
            <div class="ta-form-group">
              <label class="ta-form-label raleway-semi-p15" for="ta_message">MESSAGE COMPLÉMENTAIRE</label>
              <textarea
                id="ta_message"
                v-model="contactForm.message"
                class="ta-form-input ta-form-textarea"
                rows="5"
              ></textarea>
            </div>
          </div>

          <!-- Error / Success messages -->
          <div v-if="contactErrorMessage" class="ta-form-feedback ta-form-feedback--error raleway-reg-p13">
            {{ contactErrorMessage }}
          </div>
          <div v-if="contactSuccessMessage" class="ta-form-feedback ta-form-feedback--success raleway-reg-p13">
            {{ contactSuccessMessage }}
          </div>

          <!-- Submit -->
          <div class="ta-form-actions">
            <button
              type="submit"
              class="hortense-btn-primary ta-form-submit"
              :disabled="contactSubmitting"
            >
              {{ contactSubmitting ? 'ENVOI EN COURS...' : 'ENVOYER MA DEMANDE' }}
            </button>
          </div>

        </form>
      </div>
    </section>

  </div>

  <!-- ══════════════════════════════════════════════════════════════
       BOTTOM STICKY BAR — fixed at bottom, always on top
  ══════════════════════════════════════════════════════════════ -->
  <div class="ta-sticky-bar">
    <p class="ta-sticky-bar__text" :class="isPhone ? 'merriweather-reg-13 text-center' : 'merriweather-reg-18'">
      CRÉEZ UN VOYAGE INOUBLIABLE, ENTIÈREMENT SUR MESURE
    </p>
    <button
      @click="scrollToContact"
      class="ta-sticky-bar__btn" :class="isPhone ? 'raleway-semi-p13' : 'raleway-semi-p18'"
    >
      NOUS CONTACTER
    </button>
  </div>
  </div>
</template>

<script>
export default {
  name: 'TravelAdvisorPage',

  data() {
    return {
      isPhone: typeof window !== 'undefined' && window.innerWidth < 768,

      /* ── Section 2 data ── */
      whyCards: [
        {
          img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Offre+diversifie%CC%81e.webp',
          title: 'UNE OFFRE DIVERSIFIÉE À TRAVERS LA FRANCE',
          text: "Hôtels, chambres d'hôtes, maisons et villas : une sélection variée pour répondre à toutes les envies d'évasion."
        },
        {
          img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Offre+e%CC%81coresponsable.webp',
          title: 'UNE OFFRE ÉCORESPONSABLE DIFFÉRENCIANTE',
          text: "Proposez à vos clients des séjours alignés avec les enjeux actuels, sans compromis sur le confort ni l'expérience."
        },
        {
          img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Se%CC%81jour+sur+mesure.webp',
          title: 'DES SÉJOURS SUR MESURE, RICHES EN DÉCOUVERTES',
          text: "Nous concevons des itinéraires personnalisés, ponctués d'adresses confidentielles et d'expériences inédites, pour offrir à chaque client un voyage unique."
        },
        {
          img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Gain+de+temps.webp',
          title: 'UN GAIN DE TEMPS ET DE FLEXIBILITÉ',
          text: "Nous facilitons chaque étape, de la recherche à la coordination, grâce à un large réseau de partenaires, y compris hors ligne, pour répondre à toutes les demandes sur mesure."
        },
        {
          img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Re%CC%81seau+partenaires.webp',
          title: 'UN RÉSEAU DE PARTENAIRES DE CONFIANCE',
          text: "Nous collaborons avec des hébergements et prestataires soigneusement sélectionnés pour leur qualité et leur engagement."
        },
        {
          img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Capacite%CC%81+a%CC%80+re%CC%81pondre.webp',
          title: 'UNE CAPACITÉ À RÉPONDRE À TOUTES LES TYPOLOGIES DE DEMANDES.',
          text: "Voyages individuels, groupes, séminaires ou expériences exclusives : nous adaptons chaque proposition au contexte et aux objectifs."
        }
      ],

      /* ── Section 3 data ── */
      clientsStart: 0,
      clientsItems: [
        {
          img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Des%20lieux%20uniques.webp',
          title: 'DES ADRESSES UNIQUES',
          text: "Des hôtels de caractère, des maisons de maître, des adresses confidentielles, loin des standards du tourisme traditionnel."
        },
        {
          img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Voyage%20responsable.webp',
          title: 'UN VOYAGE PLUS RESPONSABLE',
          text: "Des hébergements engagés dans une démarche durable, sélectionnés selon des critères exigeants."
        },
        {
          img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Avantages exclusifs .webp',
          title: 'DES AVANTAGES EXCLUSIFS NÉGOCIÉS',
          text: "Grâce à nos partenariats privilégiés, vos clients bénéficient d'attentions particulières et d'avantages exclusifs."
        },
        {
          img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Se%CC%81jour+pense%CC%81+aux+de%CC%81tails.webp',
          title: 'UN SÉJOUR PENSÉ DANS LES MOINDRES DÉTAILS',
          text: "Chaque voyage est conçu en fonction des attentes, du rythme et du niveau de confort souhaité par vos clients."
        },
        {
          img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Des+expe%CC%81riences+authentiques.webp',
          title: 'DES EXPÉRIENCES AUTHENTIQUES',
          text: "Rencontres locales, activités immersives, découvertes hors des sentiers battus."
        }
      ],

      /* ── Section 5 data ── */
      destItems: [
        { img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Paris.webp', name: 'PARIS', sub: 'CONFIDENTIEL & CULTUREL' },
        { img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Provence.webp', name: 'PROVENCE', sub: 'BIEN-ÊTRE & ART DE VIVRE' },
        { img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Bourgogne.webp', name: 'BOURGOGNE', sub: 'ÉLÉGANCE & ART DU VIN' },
        { img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Co%CC%82te+atlantique.webp', name: 'CÔTE ATLANTIQUE', sub: 'NATURE & GRAND AIR' },
        { img: 'https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/travel_advisor/Corse.webp', name: 'CORSE', sub: 'SAUVAGE & PRÉSERVÉE' }
      ],

      /* ── Section 6 contact form ── */
      contactForm: {
        last_name: '',
        first_name: '',
        email: '',
        societe: '',
        nombre_adultes: '',
        nombre_enfants: '',
        age_moyen: '',
        nombre_chambres: '',
        dates_sejour: '',
        budget: '',
        destination: '',
        type_experience: '',
        message: ''
      },
      contactSubmitting: false,
      contactErrorMessage: '',
      contactSuccessMessage: ''
    };
  },

  mounted() {
    const navbarItems = document.querySelectorAll('.bm-burger-bars');

    navbarItems.forEach((bar) => {
      bar.style.color = 'white';
      bar.style.backgroundColor = 'white';
      bar.style.border = '0.5px solid white';
    });

    const whatsappBtn = document.getElementById('whatsapp-button');
	  if (whatsappBtn) whatsappBtn.style.top = '80%';
    const onResize = () => { this.isPhone = window.innerWidth < 768; };
    window.addEventListener('resize', onResize);
    this.$once('hook:beforeDestroy', () => window.removeEventListener('resize', onResize));
  },

  methods: {
    scrollToContact() {
      const contactSection = document.getElementById('nous-contacter');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    clientsPrev() {
      const vp = this.$refs.clientsViewport;
      if (!vp) return;
      const colW = vp.querySelector('.ta-clients__col').offsetWidth;
      vp.scrollBy({ left: -colW, behavior: 'smooth' });
    },
    clientsNext() {
      const vp = this.$refs.clientsViewport;
      if (!vp) return;
      const colW = vp.querySelector('.ta-clients__col').offsetWidth;
      vp.scrollBy({ left: colW, behavior: 'smooth' });
    },
    destPrev() {
      const vp = this.$refs.destViewport;
      if (!vp) return;
      const cardW = vp.querySelector('.ta-dest__card').offsetWidth + 16;
      vp.scrollBy({ left: -cardW, behavior: 'smooth' });
    },
    destNext() {
      const vp = this.$refs.destViewport;
      if (!vp) return;
      const cardW = vp.querySelector('.ta-dest__card').offsetWidth + 16;
      vp.scrollBy({ left: cardW, behavior: 'smooth' });
    },
    /* Returns the height-tier class for a clients column.
       Pattern per 5: outer(h1) inner(h2) centre(h3) inner(h2) outer(h1) */
    colHeightClass(i) {
      const pos = i % 5;
      if (pos === 2) return 'ta-clients__col--h3';
      if (pos === 1 || pos === 3) return 'ta-clients__col--h2';
      return 'ta-clients__col--h1';
    },

    async handleContactSubmit() {
      this.contactErrorMessage = '';
      this.contactSuccessMessage = '';

      if (!this.contactForm.email.trim() || !this.isValidContactEmail(this.contactForm.email)) {
        this.contactErrorMessage = 'Veuillez saisir une adresse e-mail valide.';
        return;
      }

      this.contactSubmitting = true;

      try {
        const formspreeData = {
          last_name: this.contactForm.last_name,
          first_name: this.contactForm.first_name,
          email: this.contactForm.email,
          societe: this.contactForm.societe || 'Non spécifié',
          nombre_adultes: this.contactForm.nombre_adultes || 'Non spécifié',
          nombre_enfants: this.contactForm.nombre_enfants || 'Non spécifié',
          age_moyen: this.contactForm.age_moyen || 'Non spécifié',
          nombre_chambres: this.contactForm.nombre_chambres || 'Non spécifié',
          dates_sejour: this.contactForm.dates_sejour || 'Non spécifié',
          budget: this.contactForm.budget || 'Non spécifié',
          destination: this.contactForm.destination || 'Non spécifié',
          type_experience: this.contactForm.type_experience || 'Non spécifié',
          message: this.contactForm.message || 'Non spécifié'
        };

        const FORMSPREE_ID = 'xbdpgzve';
        const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formspreeData)
        });

        if (response.ok) {
          this.contactSuccessMessage = 'Votre demande a bien été envoyée. Notre équipe revient vers vous dans les plus brefs délais.';
          this.resetContactForm();
        } else {
          this.contactErrorMessage = "Une erreur est survenue lors de l'envoi. Veuillez réessayer.";
        }
      } catch (error) {
        console.error('Error submitting contact form:', error);
        this.contactErrorMessage = "Une erreur est survenue lors de l'envoi. Veuillez réessayer.";
      } finally {
        this.contactSubmitting = false;
      }
    },

    isValidContactEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    resetContactForm() {
      this.contactForm = {
        last_name: '',
        first_name: '',
        email: '',
        societe: '',
        nombre_adultes: '',
        nombre_enfants: '',
        age_moyen: '',
        nombre_chambres: '',
        dates_sejour: '',
        budget: '',
        destination: '',
        type_experience: '',
        message: ''
      };
    }
  }
};
</script>

<style scoped>
/* ─────────────────────────────────────────────────────────────────────
   BASE
───────────────────────────────────────────────────────────────────── */
.ta-root {
  display: contents;
}
.ta-page {
  background: #ffffff;
  overflow-x: hidden;
}

/* ─────────────────────────────────────────────────────────────────────
   HERO
───────────────────────────────────────────────────────────────────── */
.ta-hero {
  position: relative;
  width: 100%;
  height: 392px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ta-hero__bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 35%;
}
.ta-hero__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.28);
}
.ta-hero__content {
  position: relative;
  z-index: 2;
  text-align: center;
  color: #ffffff;
  max-width: 1020px;
}
.ta-hero__title {
  color: #ffffff;
  margin: 0 0 14px;
  line-height: 1.2;
}
.ta-hero__sub {
  color: rgba(255, 255, 255, 0.92);
  margin: 0;
  line-height: 1.5;
  font-style: normal;
}
.ta-hero__sub em { font-style: italic; }

/* ─────────────────────────────────────────────────────────────────────
   INTRO
───────────────────────────────────────────────────────────────────── */
.ta-intro {
  background: #CFE5DC4D;
  padding: 22px 92px 22px 92px;
  text-align: center;
}
.ta-intro__text {
  color: #000;
  line-height: 1.85;
  margin: 0 auto;
}

/* ─────────────────────────────────────────────────────────────────────
   SECTION 2 — POURQUOI CHOISIR HORTENSE  (2 × 3 card grid)
   Each card = rounded white box with light border, image on top,
   title + body below inside the card
───────────────────────────────────────────────────────────────────── */
.ta-why {
  background: #ffffff;
  padding: clamp(40px, 6vw, 72px) clamp(20px, 5vw, 64px);
}
.ta-why__header {
  text-align: center;
  margin-bottom: clamp(28px, 4vw, 48px);
}
.ta-why__header h2 {
  margin: 0 0 8px;
  color: #0e0000;
}
.ta-why__sub {
  margin: 0;
  color: #333;
  font-style: normal;
}
.ta-why__sub em { font-style: italic; }

.ta-why__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin: 0 auto;
}

/* The card itself: rounded white box with subtle border */
.ta-why-card {
  border-radius: 29px;
  overflow: hidden;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  box-shadow: 0px 3px 6px #00000029;
}

/* Image fills top portion — no border-radius (clipped by card) */
.ta-why-card__img-wrap {
  width: 100%;
  height: 195px;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  flex-shrink: 0;
}
.ta-why-card__img-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.4s ease;
}
.ta-why-card:hover .ta-why-card__img-wrap img {
  transform: scale(1.04);
}

/* 5th and 6th card image positioning */
.ta-why-card:nth-child(5) .ta-why-card__img-wrap img {
  object-position: center 25%;
}

.ta-why-card:nth-child(6) .ta-why-card__img-wrap img {
  object-position: center 20%;
}

/* Text body inside the card */
.ta-why-card__body {
  padding: 18px 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ta-why-card__title {
  color: #0e0000;
  margin: 0;
  line-height: 1.4;
}
.ta-why-card__text {
  color: #444444;
  margin: 0;
  line-height: 1.65;
}

/* ─────────────────────────────────────────────────────────────────────
   SECTION 3 — POURQUOI VOS CLIENTS VONT ADORER HORTENSE
   Key rules from screenshot:
   · 5 columns visible, NO gap between images, NO border-radius
   · Columns bottom-aligned; centre col tallest, outer cols shortest
   · Height pattern (outer→inner→centre): h1 < h2 < h3
   · Text (title + body) sits below the image, outside the image box
   · Left/right arrows outside the columns
───────────────────────────────────────────────────────────────────── */
.ta-clients {
  background: #ffffff;
  padding: clamp(40px, 6vw, 72px) 0 0;
}
.ta-clients__title {
  text-align: center;
  color: #0e0000;
  margin: 0 0 clamp(28px, 4vw, 48px);
  padding: 0 20px;
}

/* Wrapper holds arrow buttons + scrollable viewport */
.ta-clients__wrapper {
  position: relative;
}

/* Scrollable viewport — clips the track */
.ta-clients__viewport {
  overflow-x: auto;
  overflow-y: visible;
  scrollbar-width: none;
}
.ta-clients__viewport::-webkit-scrollbar { display: none; }

/* Track: flex row, items top-aligned, 11px gap */
.ta-clients__track {
  display: flex;
  align-items: flex-start; /* top-aligned so offset cols drop down */
  gap: 11px;               /* 11px gap between columns             */
  padding-bottom: clamp(28px, 4vw, 48px);
}

/* Each column */
.ta-clients__col {
  flex: 0 0 20%;
  width: 306px;
  display: flex;
  flex-direction: column;
}

/* Alternate (even-index) columns shifted down ~30px */
.ta-clients__col--offset {
  margin-top: 30px;
}

/* Image box — same height for all columns, NO border-radius */
.ta-clients__img-box {
  width: 100%;
  height: 398px;
  overflow: hidden;
  border-radius: 0;
}
.ta-clients__img-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Text below image */
.ta-clients__text-box {
  padding: 14px 16px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ta-clients__item-title {
  color: #0e0000;
  margin: 0;
  line-height: 1.35;
}
.ta-clients__item-desc {
  color: #444;
  margin: 0;
  line-height: 1.6;
}

/* Arrow buttons — absolutely positioned, vertically centred on the image height */
.ta-arr {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #413E3E9D;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  color: #fff;
  z-index: 4;
  transition: background 0.2s, border-color 0.2s;
  position: absolute;
  top: calc(clamp(260px, 36vw, 460px) / 2);
  transform: translateY(-50%);
}
.ta-arr--left  { left: 8px; }
.ta-arr--right { right: 8px; }

/* ─────────────────────────────────────────────────────────────────────
   SECTION 4 — NOTRE APPROCHE / NOTRE OBJECTIF
───────────────────────────────────────────────────────────────────── */
.ta-approach {
  background: #f5f5f5;
  padding: clamp(40px, 6vw, 80px) clamp(20px, 5vw, 64px);
  display: flex;
  flex-direction: column;
  gap: clamp(40px, 6vw, 72px);
}

.ta-approach__block {
  display: grid;
  align-items: center;
  max-width: 1300px;
  margin: 0 auto;
  width: 100%;
}
.ta-approach__block--a { grid-template-columns: 42% 58%; }
.ta-approach__block--b { grid-template-columns: 58% 42%; }

/* White card */
.ta-approach__card {
  background: #ffffff;
  border-radius: 16px;
  padding: clamp(24px, 3vw, 44px) clamp(20px, 2.5vw, 40px);
  position: relative;
  z-index: 2;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  text-align: center;
}
/* Block A: card slides right over the image */
.ta-approach__block--a .ta-approach__card  { margin-right: -56px; }
/* Block B: card slides left over the image */
.ta-approach__block--b .ta-approach__card  { margin-left:  -56px; }

.ta-approach__card-title {
  margin: 0 0 14px;
  color: #0e0000;
}
.ta-approach__card-intro {
  color: #444;
  line-height: 1.75;
  margin: 0 0 18px;
}
.ta-approach__list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.ta-approach__list li {
  color: #333;
  padding: 11px 0;
  border-bottom: 1px solid #ebebeb;
  text-align: center;
  line-height: 1.5;
}
.ta-approach__list li:first-child { border-top: 1px solid #ebebeb; }

/* Rounded image */
.ta-approach__img-wrap {
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  z-index: 1;
  aspect-ratio: 4 / 3;
}
.ta-approach__img-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* ─────────────────────────────────────────────────────────────────────
   SECTION 5 — DESTINATIONS CAROUSEL
───────────────────────────────────────────────────────────────────── */
.ta-destinations {
  background: #ffffff;
  padding: clamp(40px, 6vw, 72px) 0 clamp(40px, 6vw, 72px);
}
.ta-destinations__title {
  text-align: center;
  color: #0e0000;
  margin: 0 0 clamp(24px, 3.5vw, 40px);
  padding: 0 20px;
}

.ta-dest__wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.ta-dest__viewport {
  flex: 1;
  overflow-x: auto;
  scrollbar-width: none;
  padding: 0 4px;
}
.ta-dest__viewport::-webkit-scrollbar { display: none; }
.ta-dest__track {
  display: flex;
  gap: 10px;
  padding: 0 4px 4px;
}

.ta-dest__card {
  flex: 0 0 calc(20% - 13px);
  min-width: 180px;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  aspect-ratio: 3 / 4;
  cursor: pointer;
}
.ta-dest__card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.4s ease;
}
.ta-dest__card:hover img { transform: scale(1.04); }
.ta-dest__card-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.0) 55%);
}
.ta-dest__card-label {
  position: absolute;
  bottom: 18px;
  left: 14px;
  right: 14px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 3px;
}
.ta-dest__card-name {
  color: #ffffff;
  display: block;
  line-height: 1.2;
  letter-spacing: 0.5px;
}
.ta-dest__card-sub {
  color: #fff;
  display: block;
  letter-spacing: 0.8px;
}

/* ─────────────────────────────────────────────────────────────────────
   SECTION 6 — CONTACT CTA + FORM
───────────────────────────────────────────────────────────────────── */
.ta-contact {
  background: #ffffff;
  padding: clamp(48px, 8vw, 96px) clamp(20px, 8vw, 120px);
  text-align: center;
}
.ta-contact__title {
  color: #0e0000;
  margin: 0 0 14px;
}
.ta-contact__text {
  color: #555;
  margin: 0 auto 40px;
  line-height: 1.7;
  max-width: 760px;
}

/* Form wrapper card */
.ta-contact__form-wrap {
  background: #ffffff;
  box-shadow: 0px 3px 6px #00000029;
  border-radius: 15px;
  padding: clamp(28px, 4vw, 52px) clamp(24px, 5vw, 64px);
  max-width: 900px;
  margin: 0 auto;
  text-align: left;
}
.ta-contact__form-title {
  text-align: center;
  color: #0e0000;
  margin: 0 0 20px;
}
.ta-contact__form-divider {
  border: none;
  border-top: 1px solid #D1CCCC;
  margin: 0 0 32px;
}

/* Form rows */
.ta-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}
.ta-form-row--full {
  grid-template-columns: 1fr;
}

/* Individual field group */
.ta-form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: 560px) {
    gap: 2px;
  }
}
.ta-form-label {
  color: #1a1a1a;
  font-size: 13px;
  letter-spacing: 0.5px;

  @media (max-width: 560px) {
    margin-bottom: 0;
  }
}

/* Inputs & textarea */
.ta-form-input {
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  padding: 12px 16px;
  font-family: 'Raleway', sans-serif;
  font-size: 0.95rem;
  color: #333;
  background: #ffffff;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
  width: 100%;
  box-sizing: border-box;
}
.ta-form-input:focus {
  outline: none;
  border-color: #2C5E4B;
  box-shadow: 0 0 0 3px rgba(44, 94, 75, 0.1);
}
.ta-form-input::placeholder {
  color: rgba(0, 0, 0, 0.35);
}
.ta-form-textarea {
  resize: vertical;
  min-height: 120px;
}

/* Feedback messages */
.ta-form-feedback {
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 16px;
  text-align: center;
}
.ta-form-feedback--error  { color: #D56A70; }
.ta-form-feedback--success { color: #2C5E4B; }

/* Submit button row */
.ta-form-actions {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}
.ta-form-submit {
  padding: 14px 48px;
  letter-spacing: 1.5px;
  min-width: 260px;
}
.ta-form-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ─────────────────────────────────────────────────────────────────────
   RESPONSIVE — TABLET  (≤ 900px)
───────────────────────────────────────────────────────────────────── */
@media (max-width: 900px) {
  /* Section 2 grid */
  .ta-why__grid { grid-template-columns: repeat(2, 1fr); }

  /* Section 3 — wider columns */
  .ta-clients__col { flex: 0 0 30%; }
  .ta-clients__img-box { height: clamp(200px, 30vw, 360px); }
  .ta-arr { top: calc(clamp(200px, 30vw, 360px) / 2); }

  /* Section 4 approach — stack vertically */
  .ta-approach__block--a,
  .ta-approach__block--b { grid-template-columns: 1fr; gap: 0; }

  .ta-approach__block--a .ta-approach__card  { margin-right: 0; order: 2; border-radius: 0 0 16px 16px; box-shadow: 0 6px 20px rgba(0,0,0,0.08); }
  .ta-approach__block--a .ta-approach__img-wrap { order: 1; border-radius: 16px 16px 0 0; }
  .ta-approach__block--b .ta-approach__card  { margin-left:  0; order: 2; border-radius: 0 0 16px 16px; box-shadow: 0 6px 20px rgba(0,0,0,0.08); }
  .ta-approach__block--b .ta-approach__img-wrap { order: 1; border-radius: 16px 16px 0 0; }

  /* Section 5 destinations */
  .ta-dest__card { flex: 0 0 calc(33% - 11px); }
}

/* ─────────────────────────────────────────────────────────────────────
   RESPONSIVE — MOBILE  (≤ 560px)
───────────────────────────────────────────────────────────────────── */
@media (max-width: 560px) {
  /* Hero */
  .ta-hero { height: 392px; }

  /* Intro */
  .ta-intro { padding: 24px 20px; }

  /* Section 2 — single column */
  .ta-why { padding: 32px 16px; }
  .ta-why__grid { grid-template-columns: 1fr; }

  /* Section 3 */
  .ta-clients__col { flex: 0 0 64%; }
  .ta-clients__img-box { height: clamp(180px, 52vw, 280px); }
  .ta-arr { width: 32px; height: 32px; font-size: 14px; top: 50%; }
  .ta-arr--left  { left: 4px; }
  .ta-arr--right { right: 4px; }

  /* Section 4 */
  .ta-approach { padding: 32px 16px; gap: 32px; }

  /* Section 5 */
  .ta-dest__card { flex: 0 0 72%; }

  /* Section 6 */
  .ta-contact { padding: 40px 20px; }
  .ta-contact__form-wrap { padding: 24px 16px; }
  .ta-form-row { grid-template-columns: 1fr; }
  .ta-form-submit { width: 100%; min-width: unset; }
}

/* ─────────────────────────────────────────────────────────────────────
   BOTTOM STICKY BAR
───────────────────────────────────────────────────────────────────── */
.ta-sticky-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  background: #3B6152;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px clamp(20px, 5vw, 80px);
  gap: 20px;
}

.ta-sticky-bar__text {
  color: #ffffff;
  margin: 0;
  letter-spacing: 0.5px;
  line-height: 1.4;
}

.ta-sticky-bar__btn {
   flex-shrink: 0;
   display: inline-block;
   color: #ffffff;
   border: 1px solid #ffffff;
   border-radius: 15px;
   padding: 10px 28px;
   text-decoration: none;
   letter-spacing: 1px;
   white-space: nowrap;
   transition: background 0.2s ease, color 0.2s ease;
   background: #3B6152;
   cursor: pointer;
 }
 .ta-sticky-bar__btn:hover {
   background: #ffffff;
   color: #3B6152;
 }

@media (max-width: 560px) {
  .ta-sticky-bar {
    flex-direction: column;
    align-items: flex-start;
    padding: 16px 20px;
    gap: 12px;
  }
  .ta-sticky-bar__btn {
    align-self: stretch;
    text-align: center;
  }
}

</style>
